import React, { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_SERVER_URL || '';

/**
 * PlayerContextMenu — small floating menu anchored at {x, y}.
 *
 * Props:
 *   x, y               — pixel coords (from the click / touch event)
 *   target             — { id, name } of the player the menu is for
 *   selfCharacterId    — current character's id (to suppress self-actions)
 *   onClose            — called when the menu should dismiss
 *   onWhisper(target)  — optional; if provided, shows a "Whisper" item
 *   onInspect(id)      — optional; if provided, shows a "View character" item
 *   onHideMessage      — optional; if provided, shows a "Hide message" item
 *                        (caller passes a per-message callback already bound)
 *   onReport(target)   — optional; if provided, shows a "Report player" item
 */
const PlayerContextMenu = ({
  x,
  y,
  target,
  selfCharacterId,
  onClose,
  onWhisper,
  onInspect,
  onHideMessage,
  onReport,
}) => {
  const ref = useRef(null);
  const [isBlocked, setIsBlocked] = useState(null); // null = unknown / loading
  const [busy, setBusy] = useState(false);
  const isSelf = !!(target && selfCharacterId && target.id === selfCharacterId);

  // Query current block state
  useEffect(() => {
    let cancelled = false;
    if (!target?.id || isSelf) return;
    const token = localStorage.getItem('token');
    fetch(`${API}/api/blocks/check/${target.id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { blocked: false })
      .then(data => { if (!cancelled) setIsBlocked(!!data.blocked); })
      .catch(() => { if (!cancelled) setIsBlocked(false); });
    return () => { cancelled = true; };
  }, [target?.id, isSelf]);

  // Dismiss on outside click or Escape
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const toggleBlock = async () => {
    if (busy || !target?.id) return;
    const token = localStorage.getItem('token');
    setBusy(true);
    try {
      const method = isBlocked ? 'DELETE' : 'POST';
      const res = await fetch(`${API}/api/blocks/${target.id}`, {
        method,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setIsBlocked(!isBlocked);
      }
    } catch {
      /* network errors — keep menu open so user can retry */
    } finally {
      setBusy(false);
      onClose?.();
    }
  };

  if (!target) return null;

  // Clamp to viewport
  const menuWidth = 180;
  const menuHeight = 180;
  const clampedX = Math.min(x, window.innerWidth - menuWidth - 8);
  const clampedY = Math.min(y, window.innerHeight - menuHeight - 8);

  return (
    <div
      ref={ref}
      className="player-context-menu"
      style={{
        position: 'fixed',
        left: clampedX,
        top: clampedY,
        zIndex: 10000,
        background: '#1a1a2e',
        border: '1px solid #4a4a6a',
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
        minWidth: menuWidth,
        padding: '4px 0',
        fontSize: 13,
        color: '#e0e0e0',
      }}
    >
      <div style={{
        padding: '6px 12px',
        borderBottom: '1px solid #333',
        fontWeight: 600,
        color: '#ffd777',
      }}>
        {target.name}
      </div>

      {onInspect && !isSelf && (
        <MenuItem onClick={() => { onInspect(target.id); onClose?.(); }}>
          🔍 View character
        </MenuItem>
      )}

      {onWhisper && !isSelf && (
        <MenuItem onClick={() => { onWhisper(target); onClose?.(); }}>
          💬 Whisper
        </MenuItem>
      )}

      {onHideMessage && (
        <MenuItem onClick={() => { onHideMessage(); onClose?.(); }}>
          🙈 Hide this message
        </MenuItem>
      )}

      {!isSelf && (
        <MenuItem onClick={toggleBlock} disabled={busy || isBlocked === null}>
          {isBlocked === null ? '…' : (isBlocked ? '✅ Unblock' : '🚫 Block player')}
        </MenuItem>
      )}

      {onReport && !isSelf && (
        <MenuItem onClick={() => { onReport(target); onClose?.(); }}>
          🚩 Report player
        </MenuItem>
      )}
    </div>
  );
};

const MenuItem = ({ children, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '8px 12px',
      background: 'transparent',
      border: 'none',
      color: disabled ? '#666' : '#e0e0e0',
      cursor: disabled ? 'default' : 'pointer',
      fontSize: 13,
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = '#2a2a4a'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
  >
    {children}
  </button>
);

export default PlayerContextMenu;
